import { AnimalLocationType } from 'app/domain/models/enums/AnimalLocationType';
import i18next from 'i18next';

export type NameValueList = Array<{ name: string; value: string }>;
export type HerdNameValueList = Array<{
    id: string;
    name: string;
    herdType: string;
}>;

export interface NameValueListDict {
    [key: string]: NameValueList;
}

export interface HerdNameValueListDict {
    [key: string]: HerdNameValueList;
}

export type LocationsPayload = NameValueListDict;
export type TanksPayload = NameValueList;
export type HerdsPayload = HerdNameValueList;
export type FrequencyTypePayload = NameValueList;
export type IngredientTypePayload = NameValueList;
export type HerdPensTypePayload = NameValueList;
export type FeedersTypePayLoad = NameValueList;
export type RecipesTypePayload = NameValueList;
export type YearMonthGroupDropdownTypePayload = NameValueList;
export interface HierarchyNode {
    name: string;
    id: string;
    children: HierarchyNode[];
}

export interface HerdHierarchyNode {
    name: string;
    id: string;
    herdType: string;
    children: HerdHierarchyNode[];
}

const convertName = (name: string, type: string) => {
    if (name == 'ALL') return i18next.t('filter-dropdown:all');

    if (type === 'animalLocation') {
        return i18next.t(
            'filter-dropdown:animal-location-types.' + replaceAll(name.toLowerCase(), '_', '-')
        );
    }

    const delimiter = ' ';
    return name
        .trim()
        .replace(/\s\s+/g, ' ')
        .split(delimiter)
        .map((s) => {
            const first = s[0];
            const rest = s.substr(1);
            return first.toUpperCase() + rest.toLowerCase();
        })
        .join(delimiter);
};

export const getAllString = () => convertName('ALL', '');
export const isAllString = (str: string) => str.toLowerCase() === getAllString().toLowerCase();

class HierarchyRoot {
    name: string;
    id: string;

    constructor(public children: HierarchyNode[]) {
        this.name = getAllString();
        this.id = getAllString();
    }
}

class HerdHierarchyRoot {
    name: string;
    id: string;
    type: string;

    constructor(public children: HerdHierarchyNode[]) {
        this.name = getAllString();
        this.id = getAllString();
        this.type = getAllString();
    }
}

const getRecursiveHierarchy = (
    payload: NameValueListDict,
    shouldConvertName: boolean
): HierarchyNode => {
    const hierarchy = Object.keys(payload).map((key, i): HierarchyNode => {
        const children = payload[key].map(
            (c): HierarchyNode => ({
                name: shouldConvertName ? convertName(c.name, '') : c.name,
                id: `${c.value}`,
                children: [] as HierarchyNode[]
            })
        );
        return {
            name: shouldConvertName ? convertName(key, 'animalLocation') : key,
            id: key,
            children
        };
    });

    return new HierarchyRoot(hierarchy);
};

const getRecursiveHierarchyWithoutOptionAll = (
    payload: NameValueListDict,
    shouldConvertName: boolean
): HierarchyNode => {
    const hierarchy = Object.keys(payload).map((key, i): HierarchyNode => {
        const children = payload[key].map(
            (c): HierarchyNode => ({
                name: shouldConvertName ? convertName(c.name, '') : c.name,
                id: `${c.value}`,
                children: [] as HierarchyNode[]
            })
        );
        return {
            name: shouldConvertName ? convertName(key, 'animalLocation') : key,
            id: key,
            children
        };
    });

    return hierarchy[0];
};

const getYearMonthRecursiveHierarchy = (payload: NameValueList): HierarchyNode => {
    interface YearhMonthItem {
        name: string;
        value: string;
        year: number;
        month: number;
    }

    const yearMonthItems: YearhMonthItem[] = payload.map((c): YearhMonthItem => {
        const yearMonth = c.value.split('-');
        return {
            name: c.name,
            value: c.value,
            year: parseInt(yearMonth[0].replace("'", '')),
            month: parseInt(yearMonth[1])
        };
    });

    const years = yearMonthItems.map((item) => item.year);
    const uniqueYears = years.filter((value, index, self) => self.indexOf(value) === index);

    const months = yearMonthItems.map((item) => item.month);
    const uniqueMonths = months.filter((value, index, self) => self.indexOf(value) === index);
    const hierarchy = uniqueYears.map((year) => {
        const children = uniqueMonths
            .map((month) => {
                const singleYearMonthItems = yearMonthItems.filter(
                    (item) => item.year === year && item.month === month
                );
                const children = singleYearMonthItems.map((item) => ({
                    name: item.name,
                    id: item.value,
                    children: []
                }));
                return {
                    name: month.toString(),
                    id: month.toString(),
                    children
                };
            })
            .filter((x) => x.children.length > 0);

        return {
            name: year.toString(),
            id: year.toString(),
            children
        };
    });

    hierarchy.sort((a, b) => {
        return parseInt(b.name) - parseInt(a.name);
    });

    hierarchy.map((h) => {
        h.children.sort((a, b) => {
            return parseInt(a.name) - parseInt(b.name);
        });
    });

    hierarchy.map((h) => {
        h.children.map((c) => {
            c.children.sort((a, b) => {
                const aId = parseInt(a.id.replace(/[-']/g, '').replace(/'/g, ''));
                const bId = parseInt(b.id.replace(/-/g, '').replace(/'/g, ''));
                return aId - bId;
            });
        });
    });

    hierarchy.map((h) => {
        h.children.map((c) => {
            c.name = i18next.t(`date-context:months.from-number.${c.name}`);
            return c;
        });
        return h;
    });

    return new HierarchyRoot(hierarchy);
};

export const getTanksFromPayload = (tanksPayload: TanksPayload) => {
    const children = tanksPayload.map((t) => ({
        name: convertName(t.name, ''),
        id: t.value,
        children: [] as HierarchyNode[]
    }));

    return new HierarchyRoot(children);
};

export const getHerdsFromPayload = (herdsPayload: HerdsPayload) => {
    const children = herdsPayload.map((h) => ({
        name: h.name,
        id: h.id,
        herdType: h.herdType,
        children: [] as HerdHierarchyNode[]
    }));

    return new HerdHierarchyRoot(children);
};

export const getYearhMonthMultiValueGroupDropdownPayload = (
    payload: YearMonthGroupDropdownTypePayload
) => {
    return getYearMonthRecursiveHierarchy(payload);
};

export const getLocationsFromPayload = (locPayload: LocationsPayload) => {
    return getRecursiveHierarchy(locPayload, true);
};

export const getFrequencyTypeFromPayload = (frequencyType: NameValueList) => {
    const children = frequencyType.map((v) => ({
        name: i18next.t(v.name),
        id: v.value,
        children: [] as HierarchyNode[]
    }));

    return new HierarchyRoot(children);
};

export const getDataRecordTypeFromPayload = (dataRecordType: NameValueList) => {
    const children = dataRecordType.map((v) => ({
        name: i18next.t(v.name),
        id: v.value,
        children: [] as HierarchyNode[]
    }));

    return new HierarchyRoot(children);
};

export const getIngredientTypeFromPayload = (ingredientPayload: IngredientTypePayload) => {
    const children = ingredientPayload.map((t) => ({
        name: convertName(t.name, ''),
        id: t.value,
        children: [] as HierarchyNode[]
    }));

    return new HierarchyRoot(children);
};

export const getHerdPensFromPayload = (herdPensPayload: HerdPensTypePayload) => {
    const children = herdPensPayload.map((t) => ({
        name: t.name,
        id: t.value,
        children: [] as HierarchyNode[]
    }));

    return new HierarchyRoot(children);
};

export const getFeedersFromPayload = (feedersPayload: FeedersTypePayLoad) => {
    const children = feedersPayload.map((t) => ({
        name: t.name,
        id: t.value,
        children: [] as HierarchyNode[]
    }));

    return new HierarchyRoot(children);
};

export const getRecipesFromPayload = (recipesPayload: RecipesTypePayload) => {
    const children = recipesPayload.map((t) => ({
        name: t.name,
        id: t.value,
        children: [] as HierarchyNode[]
    }));

    return new HierarchyRoot(children);
};

export const getIngredientsFromPayload = (recipesPayload: RecipesTypePayload) => {
    const children = recipesPayload.map((t) => ({
        name: t.name,
        id: t.value,
        children: [] as HierarchyNode[]
    }));

    return new HierarchyRoot(children);
};

export const getShiftsFromPayload = (shiftsPayload: RecipesTypePayload) => {
    const children = shiftsPayload.map((t) => ({
        name: t.name,
        id: t.value,
        children: [] as HierarchyNode[]
    }));

    return new HierarchyRoot(children);
};

export const getMilkShiftsFromPayload = (milkShiftsPayload: RecipesTypePayload) => {
    const children = milkShiftsPayload.map((t) => ({
        name: t.name,
        id: t.value,
        children: [] as HierarchyNode[]
    }));

    return new HierarchyRoot(children);
};

function replaceAll(string, search, replace) {
    return string.split(search).join(replace);
}
